/*
 * SafeguardsTab — AdminDashboard tab for managing inferred trading-
 * rule safeguards (Session 144e). Shows the current state of every
 * registered safeguard, last-7-day counterfactual stats, and
 * per-row Enable/Disable + Shadow-mode toggles.
 *
 * Backend: /api/admin/safeguards/{list,stats,toggle}
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Shield, ShieldCheck, ShieldOff, Activity, AlertTriangle,
  Eye, CheckCircle, XCircle, Info, RefreshCw, Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCE_META = {
  '23_VIDEO_PLAYBOOK': {
    label: 'Video Playbook',
    tone: 'text-green-400 bg-green-500/10 border-green-500/30',
  },
  INFERRED: {
    label: 'Inferred Rule',
    tone: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  },
  '17_APR_GAP_B': {
    label: 'Video Playbook',
    tone: 'text-green-400 bg-green-500/10 border-green-500/30',
  },
};

const RECOMMENDATION_META = {
  ENABLE_GATE: {
    label: 'Enable',
    tone: 'text-green-400 bg-green-500/10',
    hint: 'Counterfactual data suggests this rule saves ≥50 pts/period.',
  },
  KEEP_DISABLED: {
    label: 'Keep Off',
    tone: 'text-red-400 bg-red-500/10',
    hint: 'Counterfactual data suggests this rule costs ≥50 pts/period.',
  },
  AMBIGUOUS_TUNE_THRESHOLD: {
    label: 'Tune Threshold',
    tone: 'text-amber-400 bg-amber-500/10',
    hint: 'Near-zero net impact — consider adjusting parameters.',
  },
  INSUFFICIENT_DATA: {
    label: 'More Data Needed',
    tone: 'text-gray-400 bg-gray-500/10',
    hint: 'Fewer than 20 outcomes backfilled — keep collecting shadow data.',
  },
};

function StateBadge({ enabled, shadow }) {
  if (enabled) {
    return (
      <Badge className="bg-green-500/15 text-green-400 border-green-500/40" data-testid="safeguard-state-active">
        <ShieldCheck className="w-3 h-3 mr-1" /> Active
      </Badge>
    );
  }
  if (shadow) {
    return (
      <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/40" data-testid="safeguard-state-shadow">
        <Eye className="w-3 h-3 mr-1" /> Shadow
      </Badge>
    );
  }
  return (
    <Badge className="bg-gray-500/15 text-gray-400 border-gray-500/40" data-testid="safeguard-state-off">
      <ShieldOff className="w-3 h-3 mr-1" /> Off
    </Badge>
  );
}

function SafeguardRow({ sg, onToggle, busy }) {
  const sourceMeta = SOURCE_META[sg.source] || SOURCE_META.INFERRED;
  const recMeta = RECOMMENDATION_META[sg.recommendation] || RECOMMENDATION_META.INSUFFICIENT_DATA;
  const pnlColour = sg.net_pnl_7d_pts < 0 ? 'text-green-400'
                    : sg.net_pnl_7d_pts > 0 ? 'text-red-400'
                    : 'text-gray-400';

  return (
    <div
      className="rounded-xl border border-white/5 bg-gradient-to-br from-slate-900/60 to-slate-900/30 p-5 hover:border-white/10 transition-colors"
      data-testid={`safeguard-card-${sg.id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4 text-slate-400 shrink-0" />
            <h4 className="font-semibold text-slate-100 truncate" data-testid={`safeguard-name-${sg.id}`}>
              {sg.name}
            </h4>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">{sg.description}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Badge className={`${sourceMeta.tone} text-[10px]`}>{sourceMeta.label}</Badge>
            <Badge className="bg-slate-800/60 text-slate-300 text-[10px]">{sg.category}</Badge>
            <Badge className={`${recMeta.tone} text-[10px]`} title={recMeta.hint}>
              {recMeta.label}
            </Badge>
          </div>
        </div>
        <StateBadge enabled={sg.enabled} shadow={sg.shadow_mode} />
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4 text-center">
        <div className="rounded-lg bg-slate-800/40 border border-slate-700/40 py-2">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Would-Veto 7d</div>
          <div className="text-lg font-mono text-slate-100" data-testid={`safeguard-wv7-${sg.id}`}>
            {sg.would_veto_7d}
          </div>
        </div>
        <div className="rounded-lg bg-slate-800/40 border border-slate-700/40 py-2">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Outcomes</div>
          <div className="text-lg font-mono text-slate-300" data-testid={`safeguard-bf7-${sg.id}`}>
            {sg.outcomes_backfilled_7d}
          </div>
        </div>
        <div className="rounded-lg bg-slate-800/40 border border-slate-700/40 py-2">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Net P/L (if on)</div>
          <div className={`text-lg font-mono ${pnlColour}`} data-testid={`safeguard-pnl7-${sg.id}`}>
            {sg.net_pnl_7d_pts > 0 ? '+' : ''}{sg.net_pnl_7d_pts}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={sg.enabled ? 'destructive' : 'default'}
          disabled={busy}
          onClick={() => onToggle(sg.id, { enabled: !sg.enabled })}
          className="flex-1 min-w-[100px]"
          data-testid={`safeguard-toggle-enable-${sg.id}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" />
            : sg.enabled
              ? <>Disable <XCircle className="w-3 h-3 ml-1" /></>
              : <>Enable <CheckCircle className="w-3 h-3 ml-1" /></>
          }
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => onToggle(sg.id, { shadow_mode: !sg.shadow_mode })}
          className="flex-1 min-w-[100px]"
          data-testid={`safeguard-toggle-shadow-${sg.id}`}
        >
          <Eye className="w-3 h-3 mr-1" />
          Shadow: {sg.shadow_mode ? 'ON' : 'OFF'}
        </Button>
      </div>

      {/* Parameters — shown as read-only pills */}
      {sg.parameters && Object.keys(sg.parameters).length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-800/80">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Parameters</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(sg.parameters).map(([k, v]) => (
              <span
                key={k}
                className="text-[10px] px-2 py-0.5 rounded bg-slate-800/60 text-slate-300 border border-slate-700/60 font-mono"
              >
                {k}={String(v)}
              </span>
            ))}
          </div>
          {sg.per_symbol_overrides && Object.keys(sg.per_symbol_overrides).length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Per-symbol overrides</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(sg.per_symbol_overrides).map(([sym, override]) => (
                  <span
                    key={sym}
                    className="text-[10px] px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 font-mono"
                  >
                    {sym}: {Object.entries(override).map(([k, v]) => `${k}=${v}`).join(', ')}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SafeguardsTab() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);      // safeguard_id currently updating
  const [error, setError] = useState(null);
  const [safeguards, setSafeguards] = useState([]);

  const loadList = useCallback(async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/admin/safeguards/list`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setSafeguards(data.safeguards || []);
    } catch (e) {
      setError(e.message || 'Failed to load safeguards');
      setSafeguards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleToggle = async (safeguard_id, patch) => {
    setBusy(safeguard_id);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/admin/safeguards/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ safeguard_id, ...patch }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await loadList();
    } catch (e) {
      setError(`Toggle failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="safeguards-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-400">Loading safeguards…</span>
      </div>
    );
  }

  const enabledCount = safeguards.filter((s) => s.enabled).length;
  const shadowCount = safeguards.filter((s) => !s.enabled && s.shadow_mode).length;

  return (
    <div className="space-y-6" data-testid="safeguards-tab">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-slate-100 flex items-center gap-2">
            <Shield className="w-6 h-6 text-indigo-400" />
            Safeguards
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Rule-based vetoes layered on top of detector emissions. Shadow-mode
            logs what a rule would have done so you can measure counterfactual
            P/L before enabling it for live trading.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadList}
          data-testid="safeguards-refresh"
          className="shrink-0"
        >
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </header>

      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="rounded-xl bg-gradient-to-br from-green-900/20 to-green-900/5 border border-green-500/20 py-4">
          <div className="text-xs uppercase tracking-wide text-green-400 mb-1">Enabled</div>
          <div className="text-2xl font-mono text-green-300" data-testid="safeguards-count-enabled">
            {enabledCount}
          </div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-blue-900/20 to-blue-900/5 border border-blue-500/20 py-4">
          <div className="text-xs uppercase tracking-wide text-blue-400 mb-1">Shadow Only</div>
          <div className="text-2xl font-mono text-blue-300" data-testid="safeguards-count-shadow">
            {shadowCount}
          </div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-slate-900/40 to-slate-900/10 border border-slate-500/20 py-4">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Total</div>
          <div className="text-2xl font-mono text-slate-300" data-testid="safeguards-count-total">
            {safeguards.length}
          </div>
        </div>
      </div>

      {error && (
        <div
          className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm p-3 flex items-start gap-2"
          data-testid="safeguards-error"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {safeguards.length === 0 && !error && (
        <div
          className="rounded-lg border border-slate-700/60 bg-slate-800/30 text-slate-400 text-sm p-8 text-center"
          data-testid="safeguards-empty"
        >
          <Info className="w-6 h-6 mx-auto mb-2 text-slate-500" />
          No safeguards registered yet.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {safeguards.map((sg) => (
          <SafeguardRow
            key={sg.id}
            sg={sg}
            onToggle={handleToggle}
            busy={busy === sg.id}
          />
        ))}
      </div>

      <aside className="rounded-lg border border-slate-700/40 bg-slate-800/20 p-4 text-xs text-slate-400">
        <div className="flex items-start gap-2">
          <Activity className="w-4 h-4 mt-0.5 text-indigo-400 shrink-0" />
          <div>
            <strong className="text-slate-200">How this works:</strong> each safeguard
            runs on every LQP/Absorption/… emission. In <em>Shadow</em> mode the
            rule writes a row to <code className="font-mono bg-slate-900/60 px-1 rounded">safeguard_shadow_log</code> whenever it
            would have fired, but the live signal goes out unchanged.
            After ≥ 20 backfilled outcomes the <em>Net P/L (if on)</em> column
            tells you what enabling the rule would have done. The
            <strong className="text-slate-200"> recommendation badge</strong>  auto-computes from that.
          </div>
        </div>
      </aside>
    </div>
  );
}
